// biome-ignore-all lint/a11y/useSemanticElements: Interactive SVG roles have no native SVG equivalents; the synchronized table provides native HTML buttons.
import { type CSSProperties, type KeyboardEvent, useId, useRef, useState } from "react";
import type { ElectionResult, RegionSummary } from "../api";

export type RegionLeader = Pick<
  ElectionResult,
  "candidateCode" | "candidateName" | "partyAlias" | "colorSlot"
> & {
  votesAmount: number;
  votePercentage: number;
};

// The schematic viewBox (360×520) and the map artwork (1110×1868) have different aspect ratios,
// so the overlay stretches onto the artwork box instead of letterboxing away from it. Hexagonal
// hotspots absorb the horizontal squeeze; label glyphs need it undone.
const LABEL_UNSQUEEZE = 360 / 520 / (1110 / 1868);

export type RegionSchematicProps = {
  regions: readonly RegionSummary[];
  leaders: Readonly<Record<string, RegionLeader | undefined>>;
  legendResults: readonly ElectionResult[];
  selectedRegion: string | null;
  onSelectRegion: (regionName: string) => void;
};

export const RegionSchematic = ({
  regions,
  leaders,
  legendResults,
  selectedRegion,
  onSelectRegion,
}: RegionSchematicProps) => {
  const titleId = useId();
  const descriptionId = useId();
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const pathRefs = useRef<Array<SVGPathElement | null>>([]);
  const activeRegionName = hoveredRegion ?? selectedRegion;

  const moveFocus = (event: KeyboardEvent<SVGPathElement>, index: number) => {
    const direction =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (direction !== 0) {
      event.preventDefault();
      const nextIndex = (index + direction + regions.length) % regions.length;
      pathRefs.current[nextIndex]?.focus();
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const region = regions[index];
      if (region) {
        onSelectRegion(region.name);
      }
    }
  };

  return (
    <section className="map-panel" aria-labelledby={titleId}>
      <div className="sr-only">
        <h2 id={titleId}>Regional lead map</h2>
        <p>
          Candidate color overlays identify synthetic regional leaders. The underlying map artwork
          is decorative; reporting progress is stated in every tooltip and table row.
        </p>
      </div>

      <ul className="chart-legend sr-only" aria-label="Regional overlay color key">
        {legendResults.map((result) => (
          <li key={result.candidateCode}>
            <span className={`series-swatch series-color-${result.colorSlot}`} aria-hidden="true" />
            <span>{result.candidateName}</span>
          </li>
        ))}
      </ul>

      <div className="philippines-map-art-box">
        <img
          className="philippines-map-art"
          src="/images/philippines-heatmap.png"
          alt=""
          aria-hidden="true"
          draggable={false}
        />
        <svg
          className="region-hotspots"
          viewBox="0 0 360 520"
          preserveAspectRatio="none"
          role="group"
          aria-labelledby={`${titleId} ${descriptionId}`}
        >
          <desc id={descriptionId}>
            An interactive map of four selected Philippine regions. Use arrow keys to move between
            regions and Enter or Space to select one.
          </desc>
          {regions.map((region, index) => {
            const leader = leaders[region.name];
            const isSelected = selectedRegion === region.name;
            return (
              <path
                key={region.code}
                ref={(node) => {
                  pathRefs.current[index] = node;
                }}
                d={region.path}
                className={`region-shape series-color-${leader?.colorSlot ?? 6}`}
                vectorEffect="non-scaling-stroke"
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={`${region.displayName}. ${leader?.candidateName ?? "No leader"} leads. ${region.reporting.percent.toFixed(1)} percent reporting.`}
                onClick={() => onSelectRegion(region.name)}
                onFocus={() => setHoveredRegion(region.name)}
                onBlur={() => setHoveredRegion(null)}
                onMouseEnter={() => setHoveredRegion(region.name)}
                onMouseLeave={() => setHoveredRegion(null)}
                onKeyDown={(event) => moveFocus(event, index)}
              />
            );
          })}
          {regions.map((region) => (
            // biome-ignore lint/a11y/noAriaHiddenOnFocusable: SVG text is not focusable, and each region name is already announced by its own path's aria-label.
            <text
              key={`${region.code}-label`}
              className="region-label"
              transform={`translate(${region.center.x} ${region.center.y}) scale(${LABEL_UNSQUEEZE} 1)`}
              aria-hidden="true"
            >
              {region.name}
            </text>
          ))}
        </svg>
      </div>

      {/*
        The standing readout the hover tooltip used to be. Every region's leader, share, and
        reporting progress is on the page at once, so the numbers survive a screenshot, a touch
        screen with no hover, and a narrow column where a floating card covered the artwork.
      */}
      <div className="region-leads">
        <p className="region-leads__title">Regional leads</p>
        <ul>
          {regions.map((region) => {
            const leader = leaders[region.name];
            const reportingStyle = {
              "--lead-progress": `${Math.max(0, Math.min(region.reporting.percent, 100))}%`,
            } as CSSProperties;
            return (
              <li key={region.code}>
                <button
                  type="button"
                  className={`region-lead series-color-${leader?.colorSlot ?? 6}`}
                  data-active={activeRegionName === region.name || undefined}
                  aria-pressed={selectedRegion === region.name}
                  onClick={() => onSelectRegion(region.name)}
                  onFocus={() => setHoveredRegion(region.name)}
                  onBlur={() => setHoveredRegion(null)}
                  onMouseEnter={() => setHoveredRegion(region.name)}
                  onMouseLeave={() => setHoveredRegion(null)}
                >
                  <span className="region-lead__place">{region.displayName}</span>
                  <span className="region-lead__leader">
                    <strong>{leader?.candidateName ?? "No leader"}</strong>
                    <small>{leader?.partyAlias ?? "Awaiting synthetic leader"}</small>
                  </span>
                  <span className="region-lead__figures">
                    <strong>{leader?.votePercentage.toFixed(1) ?? "0.0"}%</strong>
                    <small>{leader?.votesAmount.toLocaleString() ?? "0"} votes</small>
                  </span>
                  <span className="region-lead__reporting" style={reportingStyle}>
                    {region.reporting.percent.toFixed(1)}% reporting
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <details className="map-table-disclosure sr-only">
        <summary>Open regional lead table</summary>
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: Focus lets keyboard users scroll the overflowed table. */}
        <section className="map-table-wrap" aria-label="Regional lead table" tabIndex={0}>
          <table className="map-table">
            <caption>Regional leaders and reporting progress</caption>
            <thead>
              <tr>
                <th scope="col">Region</th>
                <th scope="col">Leader</th>
                <th scope="col">Reporting</th>
              </tr>
            </thead>
            <tbody>
              {regions.map((region) => {
                const leader = leaders[region.name];
                return (
                  <tr key={region.code} data-selected={selectedRegion === region.name || undefined}>
                    <th scope="row">
                      <button type="button" onClick={() => onSelectRegion(region.name)}>
                        {region.displayName}
                      </button>
                    </th>
                    <td>
                      <span
                        className={`series-swatch series-color-${leader?.colorSlot ?? 6}`}
                        aria-hidden="true"
                      />
                      {leader?.candidateName ?? "No leader"}
                    </td>
                    <td>{region.reporting.percent.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </details>
    </section>
  );
};
